/**
 * @module app.shared
 */

import { bindable, autoinject, TaskQueue, computedFrom } from 'aurelia-framework';
import { fireEvent } from 'app/shared/lib/fire-events';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { isNullOrUndefined, range } from 'app/shared/lib/utilities';

export type PageChangeEvent = CustomEvent & { detail: { page: number } };
/**
 * Calculates the total number of pages required to display itemCount items on pages of page Size
 * @param itemCount number of items
 * @param pageSize number of items per page; if pageSize is undefined or 0, return page count of 1
 */
export function calculatePageCount(itemCount: number, pageSize: number): number { return pageSize > 0 ? Math.ceil(itemCount / (pageSize || 1000000)) : 1; }
const SHOW_BUTTONS = true;
const ADJACENT_BUTTONS = 2;

@autoinject()
export class EssPagerCustomElement {

    @bindable() public length = 0;
    @bindable() public pageSize = 10;
    @bindable() public reload?: Function;
    @bindable() public pageResults?: number;
    protected skipPage: number | '' = '';

    public page = 0;

    @computedFrom('page', 'length', 'pageSize')
    protected get maxRecord(): number {
        return Math.min((this.page + 1) * this.pageSize, this.length);
    }

    @computedFrom('page', 'pageSize')
    protected get minRecord(): number {
        return this.page * this.pageSize + 1;
    }

    protected areButtonsVisible = SHOW_BUTTONS;
    protected adjacentButtons: number = ADJACENT_BUTTONS;

    // refs
    protected $currentPage!: HTMLElement;

    constructor(public readonly element: Element, private readonly queue: TaskQueue, private readonly logger: AppLogger) { }

    // validate length and reload to true last page if needed
    private lengthChanged(): void {
        // if reload or pageResults are not provided, do not attempt validation
        if(!this.reload || isNullOrUndefined(this.pageResults)) return;
        if(this.page > 0 && this.pageResults === 0) {
            const lastPage = Math.max(Math.ceil(this.length / this.pageSize) - 1, 0);
            this.changePage(lastPage);
        }
    }

    // updates the page ui
    public updatePage(page: number): void {
        this.page = page;
        this.togglePageInput();
    }

    public changePage(page: number): void {
        if(page === this.page) {
            return;
        }
        const pages = this.pages(this.length, this.pageSize);
        if(page < 0 || page >= pages) {
            return this.resetPage();
        }
        const canceled = !fireEvent(this.element, 'pagechange', {
            detail: { page },
            cancelable: true
        });
        if(!canceled) {
            this.updatePage(page);
            if(this.reload) this.reload();
        }
    }

    public changePageAndFocus(page: number): void {
        this.changePage(page);
        this.focusCurrentPageButton();
        this.togglePageInput();
    }

    protected pages(count: number, size: number): number {
        return calculatePageCount(count, size);
    }

    protected isDisabled(page: number, pages: number): boolean {
        return page < 0 || page >= pages;
    }

    protected resetPage() {
        this.$currentPage.innerText = `${this.page + 1}`
    }

    protected computePages(page: number, pages: number) {
        const { adjacentButtons } = this;
        const visibleButtons = 2 * adjacentButtons + 5;

        // if total pages <= visibleButtons, we show buttons for all pages
        if(pages <= visibleButtons) {
            return range(pages);
        } else {
            return range(visibleButtons).map(n => {

                // first page is always shown
                if(n === 0) {
                    return 0;
                }

                // last page is always shown
                else if(n === visibleButtons - 1) {
                    return pages - 1;
                }

                // second button is ellipses if page > d + 2
                else if(n === 1) {
                    return page > adjacentButtons + 2 ? null : 1;
                }

                // second to last button is ellipses if page < (pages - 1) - (d + 2)
                else if(n === visibleButtons - 2) {
                    return page < pages - adjacentButtons - 3 ? null : pages - 2;
                }

                // if page is within 2d of the start (page - 0 < 2d), count from the start
                else if(page < 2 * adjacentButtons) {
                    return n;
                }

                // if page is within 2d of the end ((pages - 1) - page < 2d), count towards the end
                else if(pages - page < 2 * adjacentButtons + 1) {
                    return pages - (visibleButtons - n);
                }

                // otherwise, count around page
                else {
                    return page + n - 2 * adjacentButtons;
                }

                throw new Error;
            })
        }
    }

    protected syncPage(event: FocusEvent) {
        const value = this.$currentPage.innerText;
        const page = Number(value);
        if(isNaN(page) || page === 0) {
            return this.resetPage();
        }

        // determine if blur was triggered by loss of focus or entering a page number
        if(event.relatedTarget == null)
            this.changePageAndFocus(page - 1);
        else
            this.changePage(page - 1);
    }

    protected focusCurrentPageButton() {
        this.queue.queueTask(() => this.$currentPage?.focus());
    }

    protected skipToPage() {
        if(this.skipPage === '' || isNaN(Number(this.skipPage))) return;
        const page = Number(this.skipPage) - 1;
        this.changePageAndFocus(page);
    }

    protected togglePageInput() {
        if(this.page !== Number(this.skipPage) - 1) {
            this.skipPage = ''
        }
    }
}

